import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GeoAlt, WifiOff } from 'react-bootstrap-icons';
import { Pagination } from 'antd';
import { formatPrice } from '../../utils/formatPrice';
import type { LocationCardProps } from '../../types/task';
import { PriceStatusEnum } from '../../enums/PriceStatusEnum';

const LocationCard: React.FC<LocationCardProps> = ({ scenario, inspection }) => {
  const { t } = useTranslation(['task', 'price']);
  const isOffline = scenario === 'offline';

  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 5;

  useEffect(() => {
    setCurrentPage(1);
  }, [inspection]);

  const startIndex = (currentPage - 1) * pageSize;
  const paginatedPrices = inspection?.prices?.slice(startIndex, startIndex + pageSize) || [];

  return (
    <div className="flex flex-col items-center justify-center space-y-4 rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-600">
        {isOffline ? <WifiOff size={24} /> : <GeoAlt size={24} />}
      </div>
      <div>
        <h4 className="text-base font-semibold text-amber-900">
          {isOffline ? t('offlineMapUnavailable') : t('noCoordinateDataFound')}
        </h4>
      </div>

      {isOffline && inspection?.coordinates && (
        <div className="w-full rounded-md border border-amber-200 bg-white p-3 text-left shadow-sm">
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-800 sm:text-sm">
            <GeoAlt className="text-primary" /> {t('coordinates')}:
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-xs text-gray-700 sm:text-sm">
            <span className="whitespace-nowrap">
              <span>{t('lat')}:</span> {inspection.coordinates.lat.toFixed(6)}
            </span>
            <span className="whitespace-nowrap">
              <span>{t('long')}:</span> {inspection.coordinates.long.toFixed(6)}
            </span>
          </div>
        </div>
      )}

      {inspection?.prices && inspection.prices.length > 0 && (
        <div className="w-full rounded-md border border-amber-200 bg-white p-3 text-left shadow-sm">
          <div className="mb-2 text-sm font-semibold text-gray-800">{t('recordedPrices')}:</div>
          <div className="space-y-1 divide-y divide-gray-100 text-xs">
            {paginatedPrices.map((p: any, idx: number) => {
              const isNotFound = p?.status === PriceStatusEnum.PRODUCTNOTFOUND;

              return (
                <div key={idx} className="flex justify-between py-1.5">
                  <span className="font-medium text-gray-700">{p.product}</span>
                  <span className="font-bold text-primary">
                    {isNotFound ? (
                      <span className="font-semibold text-red-500">{t('price:priceNotFound')}</span>
                    ) : (
                      formatPrice(p.price)
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          {inspection.prices.length > 0 && (
            <div className="mt-2 flex justify-center overflow-x-auto py-1">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={inspection.prices.length}
                onChange={(page) => setCurrentPage(page)}
                size="small"
                showSizeChanger={false}
                responsive
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationCard;
